import { useState, useEffect, useRef, type FormEvent } from "react"
import { Sparkles, Loader2, FileText, CheckCircle2, ArrowRight, ArrowLeft, Plane, Ship, Truck, Home, X, MapPin, Plus, ChevronDown, BadgeCheck, Lock } from "lucide-react"
import { useCreateRfq } from "@/hooks/buyer/useCreateRfq"
import { useAuthStore } from "@/store/auth.store"
import { PromptModalCard } from "@/components/shared/PromptModalCard"
import { useFindSupplierStore } from "@/store/find-supplier.store"
import { useNavigate } from "@tanstack/react-router"
import { DatePicker } from "@/components/ui/date-picker"
import { useSearchLocation } from "@/hooks/useSearchLocation"
import { useBuyerAddresses } from "@/hooks/buyer/useBuyerAddresses"
import { AddAddressForm } from "@/components/dashboard/shared/AddAddressForm"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "@/lib/utils"
import { toast } from "sonner"
import { useCountryCurrencies } from "@/hooks/useCountryCurrencies"


export type RfqTarget =
  | { type: "single-product"; product: any; companyName?: string }
  | { type: "single-company"; company: any }
  | { type: "single-external"; seller: any }
  | { type: "single-lead"; seller: any }
  | { type: "multi-company"; companies?: any[]; externalSellers?: any[]; leadSellers?: any[] }
  | null

function getTargetName(target: RfqTarget): string {
  if (!target) return ""
  if (target.type === "single-product") {
    return target.product.productName || target.product.name || target.product.variantName || ""
  }
  if (target.type === "single-company") {
    return target.company.name || ""
  }
  if (target.type === "single-external") {
    return target.seller.sellerName || ""
  }
  if (target.type === "single-lead") {
    return target.seller.name || target.seller.sellerName || ""
  }
  if (target.type === "multi-company") {
    const names: string[] = []
    if (target.companies) names.push(...target.companies.map((c: any) => c.name).filter(Boolean))
    if (target.externalSellers) names.push(...target.externalSellers.map((s: any) => s.sellerName).filter(Boolean))
    if (target.leadSellers) names.push(...target.leadSellers.map((s: any) => s.name || s.sellerName).filter(Boolean))
    return names.join(", ")
  }
  return ""
}

const MIN_QUANTITY = 0.01
const MAX_QUANTITY = 100_000

const MIN_BUDGET = 1
const MAX_BUDGET = 1_000_000

const isIntegerUnit = (unit?: string | null): boolean => {
  if (!unit) return false
  const u = unit.toLowerCase().trim()
  return u === "pcs" || u === "box" || u.includes("piece") || u.includes("box")
}

function getQuantityError(val: string | number | "", unit?: string): string | null {
  if (val === "" || val === null || val === undefined) return null
  const str = String(val)
  if (isIntegerUnit(unit) && str.includes(".")) {
    return "Whole numbers only"
  }
  if (str.includes(".") && str.split(".")[1]?.length > 2) {
    return "Max 2 decimal"
  }
  const num = typeof val === "string" ? parseFloat(val) : Number(val)
  if (isNaN(num) || num <= 0) {
    return `Quantity must be greater than 0`
  }
  if (num > MAX_QUANTITY) {
    return `Quantity cannot exceed ${MAX_QUANTITY.toLocaleString()}`
  }
  return null
}

function getBudgetError(val: string): string | null {
  if (val === "") return null
  const num = Number(val)
  if (isNaN(num) || num < MIN_BUDGET) {
    return `Budget must be at least ${MIN_BUDGET}`
  }
  if (num > MAX_BUDGET) {
    return `Budget cannot exceed ${MAX_BUDGET.toLocaleString()}`
  }
  return null
}

const CURRENCIES = [
  { code: "INR", name: "INR - Indian Rupee" },
  { code: "USD", name: "USD - US Dollar" },
  { code: "EUR", name: "EUR - Euro" },
  { code: "GBP", name: "GBP - British Pound" },
  { code: "AED", name: "AED - UAE Dirham" },
  { code: "SGD", name: "SGD - Singapore Dollar" },
]

const QUANTITY_UNITS = [
  { code: "kg", name: "Kilogram (kg)" },
  { code: "g", name: "Gram (g)" },
  { code: "ltr", name: "Liter (ltr)" },
  { code: "ml", name: "Milliliter (ml)" },
  { code: "pcs", name: "Pieces (pcs)" },
  { code: "qtl", name: "Quintal (qtl)" },
  { code: "ton", name: "Metric Ton (ton)" },
  { code: "box", name: "Box (box)" },
]

const TIMELINE_OPTIONS = [
  "Days",
  "Weeks",
  "Months",
  "Years"
]

export interface SubmitRfqPageProps {
  target?: RfqTarget
  onSuccess?: () => void
  onCancel?: () => void
  isInline?: boolean
}

export function SubmitRfqPage({ target = null, onSuccess, onCancel, isInline = false }: SubmitRfqPageProps = {}) {
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const createRfqMutation = useCreateRfq()

  const storeSearchQuery_a = useFindSupplierStore((s) => s.searchQuery)
  const storeSearchQuery_b = useFindSupplierStore((s) => s.inputValue)
  const storeSearchQuery = storeSearchQuery_a || storeSearchQuery_b || ""

  // Form states
  const [queryMessage, setQueryMessage] = useState(() => storeSearchQuery || getTargetName(target))
  const [quantity, setQuantity] = useState<string>("")
  const [quantityUnit, setQuantityUnit] = useState("")
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false)
  const unitDropdownRef = useRef<HTMLDivElement>(null)
  const [budget, setBudget] = useState("")
  const [budgetCurrency, setBudgetCurrency] = useState("")
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false)
  const [currencySearch, setCurrencySearch] = useState("")
  const currencyDropdownRef = useRef<HTMLDivElement>(null)

  const { data: countryCurrenciesData, isLoading: isLoadingCurrencies } = useCountryCurrencies()

  const currencyOptions = (countryCurrenciesData && countryCurrenciesData.length > 0)
    ? countryCurrenciesData.map((item) => ({
        id: item.id,
        code: item.code,
        country: item.country,
        name: `${item.code} - ${item.country}`,
      }))
    : CURRENCIES

  const filteredCurrencies = currencyOptions.filter((curr) => {
    if (!currencySearch.trim()) return true
    const q = currencySearch.toLowerCase().trim()
    return curr.code.toLowerCase().includes(q) || curr.name.toLowerCase().includes(q)
  })

  const quantityError = getQuantityError(quantity, quantityUnit)
  const budgetError = getBudgetError(budget)

  const [paymentTerms, setPaymentTerms] = useState("")
  const [leadTime, setLeadTime] = useState("")
  const [validUntil, setValidUntil] = useState("")
  
  // Transport Mode & Location states
  const [preferredShipmentMode, setPreferredShipmentMode] = useState("")
  const [shipmentType, setShipmentType] = useState("")
  const [originLocation, setOriginLocation] = useState<any>(null)
  const { data: buyerAddresses = [], isLoading: loadingAddresses, refetch: refetchAddresses } = useBuyerAddresses()
  const [showInlineAddAddress, setShowInlineAddAddress] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Auto select default address
  useEffect(() => {
    if (!originLocation && Array.isArray(buyerAddresses) && buyerAddresses.length > 0) {
      const defaultAddr = buyerAddresses.find((a: any) => a.isDefault)
      if (defaultAddr) {
        setOriginLocation(defaultAddr)
      } else {
        setOriginLocation(buyerAddresses[0])
      }
    }
  }, [buyerAddresses, originLocation])

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false)
      }
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(e.target as Node)) {
        setIsUnitDropdownOpen(false)
      }
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target as Node)) {
        setIsCurrencyDropdownOpen(false)
        setCurrencySearch("")
      }
    }
    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [isDropdownOpen, isUnitDropdownOpen, isCurrencyDropdownOpen])

  const [showLocationDropdown, setShowLocationDropdown] = useState(false)
  const [showShipmentModeGrid, setShowShipmentModeGrid] = useState(false)
  const [showShipmentTypeGrid, setShowShipmentTypeGrid] = useState(false)
  const locationRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (locationRef.current && !locationRef.current.contains(e.target as Node)) {
        setShowLocationDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Wizard States
  const [step, setStep] = useState(1)
  const [draftSavedTime, setDraftSavedTime] = useState<string | null>(null)
  const headerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (headerRef.current) {
      // Small timeout ensures the DOM has fully rendered before scrolling
      setTimeout(() => {
        headerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      }, 100)
    }
  }, [step])

  // Draft auto-saving
  useEffect(() => {
    if (target) return
    const draftData = {
      queryMessage, quantity, quantityUnit, budget, budgetCurrency, paymentTerms,
      leadTime, validUntil, preferredShipmentMode, shipmentType, originLocation, step
    }
    const timer = setTimeout(() => {
      if (queryMessage || quantity || budget) {
        localStorage.setItem('rfq_wizard_draft', JSON.stringify(draftData))
        setDraftSavedTime(new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}))
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [queryMessage, quantity, quantityUnit, budget, budgetCurrency, paymentTerms, leadTime, validUntil, preferredShipmentMode, shipmentType, originLocation, step, target])

  useEffect(() => {
    if (target) return
    const saved = localStorage.getItem('rfq_wizard_draft')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        setQueryMessage(parsed.queryMessage || "")
        const restoredUnit = parsed.quantityUnit || ""
        let restoredQty = parsed.quantity !== undefined && parsed.quantity !== null ? String(parsed.quantity) : ""
        if (isIntegerUnit(restoredUnit) && restoredQty.includes(".")) {
          restoredQty = restoredQty.split(".")[0].replace(/\D/g, "")
        }
        setQuantity(restoredQty)
        setQuantityUnit(restoredUnit)
        setBudget(parsed.budget || "")
        setBudgetCurrency(parsed.budgetCurrency || "")
        setPaymentTerms(parsed.paymentTerms || "")
        setLeadTime(parsed.leadTime || "")
        setValidUntil(parsed.validUntil || "")
        setPreferredShipmentMode(parsed.preferredShipmentMode || "")
        setShipmentType(parsed.shipmentType || "")
        setOriginLocation(parsed.originLocation || null)
        setStep(parsed.step || 1)
      } catch (e) {
        // ignore
      }
    }
  }, [])

  useEffect(() => {
    if (target) {
      setQueryMessage(storeSearchQuery || getTargetName(target))
      setQuantity("")
      setQuantityUnit("")
      setBudget("")
      setBudgetCurrency("")
      setPaymentTerms("")
      setLeadTime("")
      setValidUntil("")
      setPreferredShipmentMode("")
      setShipmentType("")
      setOriginLocation(null)
      setStep(1)
    }
  }, [target, storeSearchQuery])

  const searchConfig = { label: "Search your City", url: "https://finance.devapi.zipaworld.com/api/auth/masters/city/manager" }
  
  const {
    searchTerm: locationSearchTerm,
    setSearchTerm: setLocationSearchTerm,
    results: locationResults,
    setResults: setLocationResults,
    isSearching: isSearchingLocation
  } = useSearchLocation(
    searchConfig.url, 
    500, 
    originLocation?.city || originLocation?.name || originLocation?.pincode || originLocation?.portName,
    (searchConfig as any)?.additionalPayload
  )

  useEffect(() => {
    if (originLocation && !locationSearchTerm) {
      setLocationSearchTerm(originLocation.city || originLocation.name || originLocation.pincode || originLocation.portName || "")
    }
  }, [originLocation])

  const [createdRfqId, setCreatedRfqId] = useState<string | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [queryMessageError, setQueryMessageError] = useState<string | null>(null)
  const [quantityUnitError, setQuantityUnitError] = useState<string | null>(null)
  const [budgetCurrencyError, setBudgetCurrencyError] = useState<string | null>(null)
  const [leadTimeError, setLeadTimeError] = useState<string | null>(null)
  const [locationError, setLocationError] = useState<string | null>(null)

  const handleNext = () => {
    setValidationError(null)
    setQueryMessageError(null)
    setQuantityUnitError(null)
    setBudgetCurrencyError(null)
    setLeadTimeError(null)
    setLocationError(null)

    const isClosedRfq = !!target
    if (step === 1 && !isClosedRfq && !queryMessage.trim()) {
      setQueryMessageError("Please describe what you're looking for")
      return
    }
    if (step === 1 && quantity !== "") {
      if (quantityError) {
        return
      }
      if (!quantityUnit) {
        setQuantityUnitError("Please select a quantity unit")
        return
      }
    }
    if (step === 2 && budget !== "") {
      if (budgetError) {
        return
      }
      if (!budgetCurrency) {
        setBudgetCurrencyError("Please select a currency")
        return
      }
    }
    if (step === 3 && leadTime) {
      const lDate = new Date(leadTime)
      if (isNaN(lDate.getTime()) || lDate < new Date(new Date().setHours(0,0,0,0))) {
        setLeadTimeError("Lead time must be today or a future date")
        return
      }
    }
    if (step === 4 && !originLocation) {
      setLocationError("Please select a destination address")
      return
    }
    setStep(s => Math.min(s + 1, 4))
  }

  const handleBack = () => {
    setValidationError(null)
    setQueryMessageError(null)
    setQuantityUnitError(null)
    setBudgetCurrencyError(null)
    setLeadTimeError(null)
    setLocationError(null)
    setStep(s => Math.max(s - 1, 1))
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
      return
    }
    localStorage.removeItem('rfq_wizard_draft')
    setQueryMessage("")
    setQueryMessageError(null)
    setQuantity("")
    setQuantityUnit("")
    setQuantityUnitError(null)
    setBudget("")
    setBudgetCurrency("")
    setBudgetCurrencyError(null)
    setPaymentTerms("")
    setLeadTime("")
    setLeadTimeError(null)
    setValidUntil("")
    setPreferredShipmentMode("")
    setShipmentType("")
    setOriginLocation(null)
    setLocationError(null)
    setStep(1)
    if (onSuccess) {
      onSuccess()
    } else {
      navigate({ to: "/explore" as any })
    }
  }

  const handleSubmit = (e?: FormEvent) => {
    if (e) e.preventDefault()
    setValidationError(null)
    setQueryMessageError(null)
    setQuantityUnitError(null)
    setBudgetCurrencyError(null)
    setLeadTimeError(null)
    setLocationError(null)

    const isClosedRfq = !!target
    if (!isClosedRfq && !queryMessage.trim()) {
      setQueryMessageError("Please describe what you're looking for")
      setStep(1)
      return
    }

    if (quantity !== "") {
      if (quantityError) {
        setStep(1)
        return
      }
      if (!quantityUnit) {
        setQuantityUnitError("Please select a quantity unit")
        setStep(1)
        return
      }
    }

    if (budget !== "") {
      if (budgetError) {
        setStep(2)
        return
      }
      if (!budgetCurrency) {
        setBudgetCurrencyError("Please select a currency")
        setStep(2)
        return
      }
    }

    if (leadTime) {
      const lDate = new Date(leadTime)
      if (isNaN(lDate.getTime()) || lDate < new Date(new Date().setHours(0,0,0,0))) {
        setLeadTimeError("Lead time must be today or a future date")
        setStep(3)
        return
      }
    }

    if (!originLocation) {
      setLocationError("Please select a destination address")
      setStep(4)
      return
    }

    const buyerId = (user?.buyer as { id?: string })?.id ?? user?.id ?? "string"
    // const resolvedValidUntil = validUntil ? new Date(validUntil).toISOString() : undefined

    const payload: any = {
      buyerId,
      type: target ? ("CLOSE" as const) : ("OPEN" as const),
      queryMessage: queryMessage.trim(),
      // validUntil: resolvedValidUntil,
      quantity: quantity === "" ? null : isIntegerUnit(quantityUnit) ? Math.trunc(Number(quantity)) : Number(Number(quantity).toFixed(2)),
      measureUnit: quantityUnit || undefined,
      budget: budget === "" ? undefined : Number(budget),
      currencyCode: budgetCurrency || undefined,
      paymentTerms: paymentTerms.trim() || undefined,
      leadTime: leadTime ? new Date(leadTime).toISOString() : undefined,
      destinationCityId: originLocation?.cityId || originLocation?._id || undefined,
      preferredShipmentMode: preferredShipmentMode || undefined,
      shipmentType: shipmentType || undefined,
      targets: {},
    }

    if (target) {
      if (target.type === "single-product") {
        if (target.product.productVariantId) {
          payload.targets.productVariantId = target.product.productVariantId
        } else {
          payload.targets.productId = target.product.id
        }
      } else if (target.type === "single-company") {
        payload.targets.companyIds = [target.company.id]
      } else if (target.type === "single-external") {
        payload.targets.externalSearchId = target.seller.externalSearchId || undefined
        if (target.seller.id) {
          payload.targets.externalSellerIds = [target.seller.id]
        }
      } else if (target.type === "single-lead") {
        payload.targets.externalSellerIds = [target.seller.id]
      } else if (target.type === "multi-company") {
        if (target.companies && target.companies.length > 0) {
          payload.targets.companyIds = target.companies.map((c) => c.id)
        }
        if (target.externalSellers && target.externalSellers.length > 0) {
          payload.targets.externalSearchId = (target as any).externalSearchId || undefined
        }
        if (target.leadSellers && target.leadSellers.length > 0) {
          payload.targets.externalSellerIds = target.leadSellers.map((s) => s.id)
        }
      }
    }

    // Safety: externalSellerIds always means a directed (CLOSED) RFQ
    if (payload.targets.externalSellerIds?.length) {
      payload.type = "CLOSE"
    }

    console.log("Submitting wizard RFQ payload (SubmitRfqPage):", payload)
    createRfqMutation.mutate(payload, {
      onSuccess: (data) => {
        const id = (data as any)?.buyerToAdminRfq?.id || data?.id || null
        setCreatedRfqId(id)
        localStorage.removeItem('rfq_wizard_draft')
        toast.success("RFQ Submitted Successfully")
        
        // Notify parent context if callbacks exist (so they reset local state variables)
        if (onSuccess) {
          onSuccess()
        }
        
        // Always trigger universal dashboard redirection to the RFQ list tab
        navigate({ to: "/dashboard/quotations" as any })
      },
      onError: (err: any) => {
        toast.error(err?.message || "Failed to submit RFQ")
      }
    })
  }

  const steps = [
    "Product",
    "Trade",
    "Timeline",
    "Transport"
  ]

  const variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.2 } }
  }

  // Anonymous visitors are prompted to log in before submitting a quote request
  if (!user) {
    return (
      <PromptModalCard onCancel={onCancel} />
    )
  }

  return (
    <div className={cn("w-full mx-auto flex flex-col pb-4", isInline ? "max-w-4xl" : "max-w-[98%] min-h-0 sm:min-h-[calc(100vh-120px)]")}>
      {/* Header & Progress */}
      <div ref={headerRef} className="mb-4 space-y-2 scroll-mt-24">
        {/* Progress Bar */}
        <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden shadow-inner">
          <motion.div 
            className="h-full bg-primary"
            initial={{ width: "0%" }}
            animate={{ width: `${((step - 1) / (steps.length - 1)) * 100}%` }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          />
        </div>
        <div className="flex justify-between text-[8px] sm:text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1">
          {steps.map((s, i) => (
            <span key={s} className={`transition-colors duration-300 text-center w-1/4 ${step >= i + 1 ? "text-primary font-bold" : ""}`}>
              {s}
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 flex-1">
        {/* Left Side - Wizard Form */}
        <div className="flex-1 min-h-[410px] sm:min-h-[240px] bg-white rounded-xl border border-slate-200/60 shadow-sm p-4 relative flex flex-col">
          {validationError && (
            <motion.div 
              initial={{ opacity: 0, y: -10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              className="mb-5 sm:mb-6 rounded-lg border border-primary/20 bg-primary/5 p-3 text-[12px] text-primary font-medium flex items-center justify-between gap-3 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div className="size-2 rounded-full bg-primary shrink-0" />
                <span>{validationError}</span>
              </div>
              <button
                type="button"
                onClick={() => setValidationError(null)}
                className="p-1 rounded-md hover:bg-primary/10 text-primary transition-colors cursor-pointer"
                title="Dismiss"
              >
                <X className="size-3.5" />
              </button>
            </motion.div>
          )}

          <div className="flex-1 relative">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="step1" variants={variants} initial="hidden" animate="visible" exit="exit" className="space-y-5">
                  <div className="space-y-1">
                    <h2 className="text-[16px] font-bold text-slate-800 tracking-tight leading-tight">
                      What are you looking for? {!target && <span className="text-primary">*</span>}
                    </h2>
                    <p className="text-slate-500 text-[12px]">Please be as specific as possible (e.g., specifications, material, quality).</p>
                  </div>
                  <div className="space-y-1">
                    <textarea
                      autoFocus
                      value={queryMessage}
                      onChange={(e) => {
                        setQueryMessage(e.target.value)
                        if (queryMessageError && e.target.value.trim()) {
                          setQueryMessageError(null)
                        }
                      }}
                      placeholder="e.g., I am looking for 100% Cotton T-shirts, 200GSM, in varied sizes..."
                      className={cn(
                        "w-full min-h-[120px] sm:min-h-[100px] resize-none rounded-xl border bg-slate-50/50 p-3 text-[12px] text-slate-800 outline-none placeholder:text-slate-400 placeholder:text-[12px] placeholder:font-normal focus:bg-white focus:ring-2 transition-all shadow-sm",
                        queryMessageError
                          ? "border-primary focus:border-primary focus:ring-primary/10"
                          : "border-slate-200 focus:border-primary/60 focus:ring-primary/10"
                      )}
                    />
                    {queryMessageError && (
                      <p className="text-[11px] text-primary font-semibold mt-1">
                        {queryMessageError}
                      </p>
                    )}
                  </div>
                  
                  <motion.div 
                    className="pt-5 border-t border-slate-100 space-y-1 max-w-xs"
                  >
                    <label className="text-[14px] font-bold tracking-wider text-slate-700">How much quantity do you require?</label>
                    <div className={cn(
                      "flex items-center h-9 rounded-xl border bg-slate-50/50 focus-within:bg-white focus-within:ring-2 transition-all shadow-sm overflow-visible pr-1",
                      quantityError || quantityUnitError
                        ? "border-primary focus-within:border-primary focus-within:ring-primary/10"
                        : "border-slate-200 focus-within:border-primary/60 focus-within:ring-primary/10"
                    )}>
                      <input
                        type="text"
                        inputMode={isIntegerUnit(quantityUnit) ? "numeric" : "decimal"}
                        value={quantity}
                        onChange={(e) => {
                          let val = e.target.value
                          if (isIntegerUnit(quantityUnit)) {
                            val = val.split(".")[0].replace(/\D/g, "")
                          } else {
                            val = val.replace(/[^0-9.]/g, "")
                            const parts = val.split(".")
                            if (parts.length > 2) {
                              val = parts[0] + "." + parts.slice(1).join("")
                            }
                            if (parts.length > 1 && parts[1].length > 2) {
                              val = parts[0] + "." + parts[1].slice(0, 2)
                            }
                          }
                          setQuantity(val)
                          if (quantityUnitError && !val) {
                            setQuantityUnitError(null)
                          }
                        }}
                        onKeyDown={(e) => {
                          if (["-", "+", "e", "E"].includes(e.key)) {
                            e.preventDefault()
                          }
                          if (isIntegerUnit(quantityUnit) && (e.key === "." || e.key === ",")) {
                            e.preventDefault()
                          }
                          if (e.key === "." && quantity.includes(".")) {
                            e.preventDefault()
                          }
                        }}
                        placeholder={isIntegerUnit(quantityUnit) ? "e.g. 500" : "e.g. 500.50"}
                        className="flex-1 min-w-0 h-full bg-transparent px-3 text-[12px] outline-none border-0 focus:ring-0 font-semibold placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                      />
                      <div className="h-4 w-[1px] bg-slate-200 mx-1"></div>
                      <div className="relative shrink-0" ref={unitDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsUnitDropdownOpen(!isUnitDropdownOpen)}
                          title={quantityUnit || "Unit"}
                          className="h-7 flex items-center justify-between gap-1 px-2.5 rounded-lg hover:bg-slate-100/80 text-[12px] font-semibold text-slate-700 transition-colors cursor-pointer"
                        >
                          <span className="truncate max-w-[70px]">{quantityUnit || "Unit"}</span>
                          <ChevronDown className="size-3 text-slate-400 shrink-0" />
                        </button>
                        {isUnitDropdownOpen && (
                          <div className="absolute right-0 mt-1.5 w-44 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-[132px] overflow-y-auto py-1 text-[12px] font-semibold text-slate-700 text-left">
                            <button
                              type="button"
                              onClick={() => {
                                setQuantityUnit("")
                                setIsUnitDropdownOpen(false)
                              }}
                              className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-slate-400 font-bold border-b border-slate-100 transition-colors cursor-pointer"
                            >
                              Unit
                            </button>
                            {QUANTITY_UNITS.map((unit) => (
                              <button
                                key={unit.code}
                                type="button"
                                onClick={() => {
                                  const newUnit = unit.code
                                  setQuantityUnit(newUnit)
                                  setQuantityUnitError(null)
                                  setIsUnitDropdownOpen(false)
                                  if (isIntegerUnit(newUnit) && quantity.includes(".")) {
                                    setQuantity(quantity.split(".")[0].replace(/\D/g, ""))
                                  }
                                }}
                                title={unit.name}
                                className={cn(
                                  "w-full text-left px-3 py-1.5 hover:bg-slate-50 transition-colors cursor-pointer truncate",
                                  quantityUnit === unit.code ? "text-primary bg-primary/5 font-bold" : ""
                                )}
                              >
                                {unit.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {(quantityError || quantityUnitError) && (
                      <p className="text-[11px] text-primary font-semibold mt-1">
                        {quantityError || quantityUnitError}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {[100, 500, 1000, 5000].map((num) => (
                        <button
                          key={num}
                          type="button"
                          onClick={() => setQuantity(String(num))}
                          className="px-2 py-0.5 rounded bg-slate-100 hover:bg-primary/10 hover:text-primary text-[11px] font-bold text-slate-500 transition-colors cursor-pointer"
                        >
                          {num.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="step2" variants={variants} initial="hidden" animate="visible" exit="exit" className="space-y-4 sm:space-y-5">
                  <div className="space-y-1">
                    <h2 className="text-[16px] font-bold text-slate-800 tracking-tight leading-tight">Trade & Budget Details</h2>
                    <p className="text-slate-500 text-[12px]">Help suppliers understand your business terms.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-700">What is your target budget?</label>
                      <div className={cn(
                        "flex items-center h-9 rounded-xl border bg-slate-50/50 focus-within:bg-white focus-within:ring-2 transition-all shadow-sm overflow-visible pr-1",
                        budgetError || budgetCurrencyError
                          ? "border-primary focus-within:border-primary focus-within:ring-primary/10"
                          : "border-slate-200 focus-within:border-primary/60 focus-within:ring-primary/10"
                      )}>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={budget}
                          onChange={(e) => {
                            setBudget(e.target.value.replace(/\D/g, ""))
                            if (budgetCurrencyError && !e.target.value) {
                              setBudgetCurrencyError(null)
                            }
                          }}
                          onKeyDown={(e) => {
                            if (["-", "+", "e", "E", "."].includes(e.key)) {
                              e.preventDefault()
                            }
                          }}
                          placeholder="e.g. 350"
                          className="flex-1 min-w-0 h-full bg-transparent px-3 text-[12px] outline-none border-0 focus:ring-0 font-semibold placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                        />
                        <div className="h-4 w-[1px] bg-slate-200 mx-1"></div>
                        <div className="relative shrink-0" ref={currencyDropdownRef}>
                          <button
                            type="button"
                            onClick={() => setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)}
                            title={budgetCurrency || "Currency"}
                            className="h-7 flex items-center justify-between gap-1 px-2.5 rounded-lg hover:bg-slate-100/80 text-[12px] font-semibold text-slate-700 transition-colors cursor-pointer"
                          >
                            <span className="truncate max-w-[70px]">{budgetCurrency || "Currency"}</span>
                            <ChevronDown className="size-3 text-slate-400 shrink-0" />
                          </button>
                          {isCurrencyDropdownOpen && (
                            <div className="absolute right-0 mt-1.5 w-56 sm:w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50 text-[12px] font-semibold text-slate-700 text-left overflow-hidden flex flex-col">
                              {/* Search Header - solid filled background */}
                              <div className="p-2 bg-slate-50 border-b border-slate-200 shrink-0">
                                <input
                                  type="text"
                                  value={currencySearch}
                                  onChange={(e) => setCurrencySearch(e.target.value)}
                                  placeholder="Search currency or country..."
                                  className="w-full px-2.5 py-1 text-[11px] rounded bg-white border border-slate-200 outline-none focus:border-primary text-slate-700 placeholder:text-slate-400"
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                />
                              </div>

                              {/* Scrollable list strictly below the header */}
                              <div className="max-h-[160px] overflow-y-auto py-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setBudgetCurrency("")
                                    setIsCurrencyDropdownOpen(false)
                                    setCurrencySearch("")
                                  }}
                                  className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-slate-400 font-bold border-b border-slate-100 transition-colors cursor-pointer"
                                >
                                  Currency
                                </button>
                                {isLoadingCurrencies && (
                                  <div className="px-3 py-2 text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
                                    <Loader2 className="size-3 animate-spin text-primary" />
                                    <span>Loading currencies...</span>
                                  </div>
                                )}
                                {filteredCurrencies.map((curr, idx) => (
                                  <button
                                    key={(curr as any).id ?? `${curr.code}-${idx}`}
                                    type="button"
                                    onClick={() => {
                                      setBudgetCurrency(curr.code)
                                      setBudgetCurrencyError(null)
                                      setIsCurrencyDropdownOpen(false)
                                      setCurrencySearch("")
                                    }}
                                    title={curr.name}
                                    className={cn(
                                      "w-full text-left px-3 py-1.5 hover:bg-slate-50 transition-colors cursor-pointer truncate",
                                      budgetCurrency === curr.code ? "text-primary bg-primary/5 font-bold" : ""
                                    )}
                                  >
                                    {curr.name}
                                  </button>
                                ))}
                                {!isLoadingCurrencies && filteredCurrencies.length === 0 && (
                                  <div className="px-3 py-2 text-[11px] text-slate-400 text-center">
                                    No currency found
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {(budgetError || budgetCurrencyError) && (
                        <p className="text-[11px] text-primary font-semibold mt-1">
                          {budgetError || budgetCurrencyError}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {["30000", "50000", "100000"].map((item) => (
                          <button
                            key={item}
                            type="button"
                            onClick={() => setBudget(item)}
                            className="px-2 py-0.5 rounded bg-slate-100 hover:bg-primary/10 hover:text-primary text-[11px] font-bold text-slate-500 transition-colors cursor-pointer"
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Preferred Payment Terms?</label>
                      <input
                        type="text"
                        value={paymentTerms}
                        onChange={(e) => setPaymentTerms(e.target.value)}
                        placeholder="e.g. 30% Advance"
                        className="w-full h-9 rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-[12px] outline-none focus:border-primary/60 focus:bg-white focus:ring-2 focus:ring-primary/10 transition-all shadow-sm font-semibold placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                      />
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {["30% Advance, 70% LC", "Net 30", "100% Advance"].map((item) => (
                          <button
                            key={item}
                            type="button"
                            onClick={() => setPaymentTerms(item)}
                            className="px-2 py-0.5 rounded bg-slate-100 hover:bg-primary/10 hover:text-primary text-[11px] font-bold text-slate-500 transition-colors cursor-pointer"
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                    </div>

                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div key="step3" variants={variants} initial="hidden" animate="visible" exit="exit" className="space-y-4 sm:space-y-5">
                  <div className="space-y-1">
                    <h2 className="text-[16px] font-bold text-slate-800 tracking-tight leading-tight">Timeline</h2>
                    <p className="text-slate-500 text-[12px]">When do you need the products delivered?</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Required Lead Time</label>
                      <div className="flex-1">
                        <DatePicker
                          value={leadTime}
                          min={new Date().toISOString().split("T")[0]}
                          onChange={(val) => {
                            setLeadTime(val)
                            if (leadTimeError) setLeadTimeError(null)
                          }}
                          placeholder="Select delivery target date"
                        />
                      </div>
                      {leadTimeError && (
                        <p className="text-[11px] text-primary font-semibold mt-1">
                          {leadTimeError}
                        </p>
                      )}
                    </div>
                    {/* <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-700">When does this RFQ expire?</label>
                      <div className="flex-1">
                        <DatePicker
                          value={validUntil}
                          min={new Date().toISOString().split("T")[0]}
                          onChange={setValidUntil}
                          placeholder="Select expiration date"
                        />
                      </div>
                    </div> */}
                  </div>
                </motion.div>
              )}

              {step === 4 && (
                <motion.div key="step4" variants={variants} initial="hidden" animate="visible" exit="exit" className="space-y-4 sm:space-y-5">
                  <div className="space-y-1">
                    <h2 className="text-[16px] font-bold text-slate-800 tracking-tight leading-tight">Transportation & Delivery</h2>
                    <p className="text-slate-500 text-[12px]">Please select your shipping preferences and city destination.</p>
                  </div>

                  <div className="space-y-4 sm:space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      <div className="space-y-2 sm:col-span-2">
                        {(showShipmentModeGrid || !!preferredShipmentMode) ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setShowShipmentModeGrid(false)
                                setPreferredShipmentMode("")
                              }}
                              className="text-[12px] font-bold hover:text-primary cursor-pointer transition-colors flex items-center gap-1.5 py-1 mb-1"
                            >
                              <span className="text-[16px] leading-none mb-0.5">-</span> Add Shipment Mode 
                            </button>
                            <div className="grid grid-cols-2 gap-2.5 sm:gap-3 pt-1 pb-1">
                              {[
                                { value: "AIR", label: "Air Freight", icon: Plane, desc: "Fastest delivery via air cargo" },
                                { value: "OCEAN", label: "Ocean Freight", icon: Ship, desc: "Cost-effective for large volumes" }
                              ].map((opt) => {
                                const IconComponent = opt.icon
                                const isSelected = preferredShipmentMode === opt.value
                                return (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => setPreferredShipmentMode(isSelected ? "" : opt.value)}
                                    className={cn(
                                      "flex items-center gap-2.5 sm:gap-3 p-3 rounded-2xl border-2 text-left transition-all duration-300 relative overflow-hidden group cursor-pointer",
                                      isSelected
                                        ? "border-primary bg-primary/5 text-primary shadow-md shadow-primary/5"
                                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-600 bg-white"
                                    )}
                                  >
                                    <IconComponent className={cn("size-5 shrink-0 transition-transform duration-300 group-hover:scale-110", isSelected ? "text-primary" : "text-slate-400")} />
                                    <div className="min-w-0 flex-1">
                                      <span className="text-[13px] font-bold block truncate">{opt.label}</span>
                                      <span className="text-[10px] text-slate-400 block truncate">{opt.desc}</span>
                                    </div>
                                    {isSelected && (
                                      <div className="absolute top-1.5 right-1.5 size-4 rounded-full bg-primary flex items-center justify-center">
                                        <CheckCircle2 className="size-2.5 text-white" />
                                      </div>
                                    )}
                                  </button>
                                )
                              })}
                            </div>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setShowShipmentModeGrid(true)}
                            className="text-[12px] font-bold text-slate-600 hover:text-primary cursor-pointer transition-colors flex items-center gap-1.5 py-1"
                          >
                             <span className="text-[16px] leading-none mb-0.5">+</span>Add Shipment Mode
                          </button>
                        )}
                      </div>

                      <div className="space-y-2 sm:col-span-2">
                        {(showShipmentTypeGrid || !!shipmentType) ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setShowShipmentTypeGrid(false)
                                setShipmentType("")
                              }}
                              className="text-[12px] font-bold hover:text-primary cursor-pointer transition-colors flex items-center gap-1.5 py-1 mb-1"
                            >
                              <span className="text-[16px] leading-none mb-0.5">-</span>Add Shipment Type
                            </button>
                            <div className="grid grid-cols-2 gap-2.5 sm:gap-3 pt-1 pb-1">
                              {[
                                { value: "DOORSTEP_DELIVERY", label: "Doorstep Delivery", icon: Home, desc: "Delivery directly to your address" },
                                { value: "NON_DOORSTEP_DELIVERY", label: "Non-Doorstep Delivery", icon: Truck, desc: "Port-to-port or terminal pickup" }
                              ].map((opt) => {
                                const IconComponent = opt.icon
                                const isSelected = shipmentType === opt.value
                                return (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => setShipmentType(isSelected ? "" : opt.value)}
                                    className={cn(
                                      "flex items-center gap-2.5 sm:gap-3 p-3 rounded-2xl border-2 text-left transition-all duration-300 relative overflow-hidden group cursor-pointer",
                                      isSelected
                                        ? "border-primary bg-primary/5 text-primary shadow-md shadow-primary/5"
                                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-600 bg-white"
                                    )}
                                  >
                                    <IconComponent className={cn("size-5 shrink-0 transition-transform duration-300 group-hover:scale-110", isSelected ? "text-primary" : "text-slate-400")} />
                                    <div className="min-w-0 flex-1">
                                      <span className="text-[13px] font-bold block truncate">{opt.label}</span>
                                      <span className="text-[10px] text-slate-400 block truncate">{opt.desc}</span>
                                    </div>
                                    {isSelected && (
                                      <div className="absolute top-1.5 right-1.5 size-4 rounded-full bg-primary flex items-center justify-center">
                                        <CheckCircle2 className="size-2.5 text-white" />
                                      </div>
                                    )}
                                  </button>
                                )
                              })}
                            </div>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setShowShipmentTypeGrid(true)}
                            className="text-[12px] font-bold text-slate-600 hover:text-primary cursor-pointer transition-colors flex items-center gap-1.5 py-1"
                          >
                            <span className="text-[16px] leading-none mb-0.5">+</span> Add Shipment Type
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="space-y-1.5 relative w-full max-w-[430px]">
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-700">
                          Destination Address
                        </label>
                        {!showInlineAddAddress && (
                          <button
                            type="button"
                            onClick={() => setShowInlineAddAddress(true)}
                            className="text-[9.5px] font-bold text-primary hover:underline cursor-pointer flex items-center gap-1"
                          >
                            <Plus className="size-3" /> Add Address
                          </button>
                        )}
                      </div>

                      {showInlineAddAddress ? (
                        <div className="border border-primary/20 rounded-xl p-3.5 sm:p-4 bg-primary/[0.02] mt-1 text-left">
                          <AddAddressForm
                            onSuccess={(newAddr) => {
                              refetchAddresses()
                              setShowInlineAddAddress(false)
                              if (newAddr) {
                                setOriginLocation(newAddr)
                                if (locationError) setLocationError(null)
                              }
                            }}
                            onCancel={() => setShowInlineAddAddress(false)}
                            isInline={false}
                            type="buyer"
                          />
                        </div>
                      ) : loadingAddresses ? (
                        <div className="flex items-center gap-2 py-2 px-3 border border-slate-100 rounded-lg text-slate-400 bg-white">
                          <Loader2 className="size-3.5 animate-spin" />
                          <span className="text-xs">Loading addresses...</span>
                        </div>
                      ) : Array.isArray(buyerAddresses) && buyerAddresses.length > 0 ? (
                        <div ref={dropdownRef} className="relative">
                          <button
                            type="button"
                            onClick={() => setIsDropdownOpen((p) => !p)}
                            className={cn(
                              "w-full flex items-center justify-between rounded-xl border bg-white px-3 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200 cursor-pointer",
                              locationError
                                ? "border-primary focus:border-primary"
                                : "border-slate-200 focus:border-primary"
                            )}
                          >
                            {originLocation ? (
                              <div className="flex items-center gap-1.5 truncate">
                                <MapPin className="size-3.5 text-primary shrink-0" />
                                <span className="font-bold text-slate-800 truncate">
                                  {originLocation.title}:
                                </span>
                                <span className="text-slate-500 truncate">
                                  {[
                                    originLocation.addressLine1,
                                    originLocation.city,
                                    originLocation.state,
                                    originLocation.country,
                                  ]
                                    .filter(Boolean)
                                    .join(", ")}
                                </span>
                                {originLocation.isDefault && (
                                  <BadgeCheck className="size-3.5 text-primary shrink-0" />
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400">Select destination address...</span>
                            )}
                            <ChevronDown className={`size-4 text-slate-400 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                          </button>

                          {isDropdownOpen && (
                            <ul className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg overscroll-contain">
                              {buyerAddresses.map((addr: any) => (
                                <li
                                  key={addr.id}
                                  onClick={() => {
                                    setOriginLocation(addr)
                                    if (locationError) setLocationError(null)
                                    setIsDropdownOpen(false)
                                  }}
                                  className={`px-3 py-2 text-[12px] font-medium text-slate-700 hover:bg-primary/5 hover:text-primary cursor-pointer transition-colors border-b border-slate-50 last:border-0 flex items-center justify-between gap-2 ${
                                    originLocation?.id === addr.id ? "bg-slate-50 text-primary font-bold" : ""
                                  }`}
                                >
                                  <div className="truncate text-left">
                                    <div className="font-bold text-slate-800 text-xs">
                                      {addr.title}
                                    </div>
                                    <div className="text-[11px] text-slate-500 truncate">
                                      {[
                                        addr.addressLine1,
                                        addr.city,
                                        addr.state,
                                        addr.postalCode,
                                        addr.country,
                                      ]
                                        .filter(Boolean)
                                        .join(", ")}
                                    </div>
                                  </div>
                                  {addr.isDefault && (
                                    <BadgeCheck className="size-4 text-primary shrink-0" />
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowInlineAddAddress(true)}
                            className="w-full flex items-center justify-between rounded-lg border bg-white px-3 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200 cursor-pointer border-slate-200 focus:border-primary"
                          >
                            <div className="flex items-center gap-1.5 truncate">
                              <MapPin className="size-3.5 text-slate-400 shrink-0" />
                              <span className="text-slate-400">No Address Available</span>
                            </div>
                          </button>
                        </div>
                      )}
                      {locationError && (
                        <p className="text-[11px] text-primary font-semibold mt-1">
                          {locationError}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

            </AnimatePresence>
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-2 mt-3.5 sm:mt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={step === 1 ? handleCancel : handleBack}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-bold text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            >
              {step === 1 ? "Cancel" : <><ArrowLeft className="size-3.5" /> Back</>}
            </button>
            
            {step < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-[12px] font-bold text-white bg-slate-900 hover:bg-slate-800 transition-all active:scale-95 shadow-md shadow-slate-900/20 sm:mr-10 lg:mr-0"
              >
                Next Step <ArrowRight className="size-3.5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={createRfqMutation.isPending}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-[12px] font-bold text-primary-foreground bg-primary hover:bg-primary-hover transition-all active:scale-95 shadow-md shadow-primary/25 disabled:opacity-50 sm:mr-10 lg:mr-0 cursor-pointer"
              >
                {createRfqMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Sparkles className="size-3.5" />}
                Submit RFQ Now
              </button>
            )}
          </div>
        </div>

        {/* Right Side - Summary Panel */}
        <div className="hidden lg:flex lg:flex-col min-h-[240px] w-[320px] shrink-0">
          <div className="h-full bg-slate-50/50 backdrop-blur-xl rounded-xl border border-slate-200/60 p-4 shadow-sm flex flex-col gap-4">
            <h3 className="text-[12px] font-bold text-slate-800 uppercase tracking-widest border-b border-slate-200/80 pb-2 flex items-center gap-2">
              <FileText className="size-3.5" />
              RFQ Summary
            </h3>
            
            <div className="space-y-4 flex-1">
              {/* Target info if present */}
              {target && (
                <div className="space-y-2 pl-1 pb-2 border-b border-slate-200/60">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block mb-0.5">Target</span>
                  {target.type === "single-product" && (
                    <div>
                      <p className="text-[12px] font-bold text-slate-500 truncate" title={target.product.productName || target.product.name}>
                        {target.product.productName || target.product.name}
                      </p>
                      {target.companyName && (
                        <p className="text-[10px] text-slate-400 truncate" title={`by ${target.companyName}`}>by {target.companyName}</p>
                      )}
                    </div>
                  )}
                  {target.type === "single-company" && (
                    <p className="text-[12px] font-bold text-slate-500 truncate" title={target.company.name}>{target.company.name}</p>
                  )}
                  {target.type === "single-external" && (
                    <p className="text-[12px] font-bold text-slate-500 truncate" title={`${target.seller.sellerName} (External)`}>{target.seller.sellerName} (External)</p>
                  )}
                  {target.type === "single-lead" && (
                    <p className="text-[12px] font-bold text-slate-500 truncate" title={`${target.seller.name || target.seller.sellerName} (Lead)`}>{target.seller.name || target.seller.sellerName} (Lead)</p>
                  )}
                  {target.type === "multi-company" && (
                    <p 
                      className="text-[12px] font-bold text-slate-500 truncate"
                      title={((target.companies || []).map((c: any) => c.name))
                        .concat((target.externalSellers || []).map((s: any) => s.sellerName))
                        .concat((target.leadSellers || []).map((s: any) => s.name || s.sellerName))
                        .filter(Boolean)
                        .join(", ")}
                    >
                      {((target.companies?.length || 0) + (target.externalSellers?.length || 0) + (target.leadSellers?.length || 0))} Selected Suppliers
                    </p>
                  )}
                </div>
              )}

              {/* Product Info */}
              <div className="space-y-3">
                <div className="flex items-start gap-2.5">
                  <div className="space-y-0.5 w-full">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block">Requirement</span>
                    <p 
                      className="text-[13px] font-semibold text-slate-500 truncate leading-snug"
                      title={queryMessage || undefined}
                    >
                      {queryMessage || <span className="text-slate-300">-</span>}
                    </p>
                  </div>
                </div>

                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block mb-0.5">Quantity</span>
                  <p className="text-[13px] font-bold text-slate-500 truncate" title={quantity ? `${quantity} ${quantityUnit}`.trim() : undefined}>
                    {quantity ? `${quantity} ${quantityUnit}`.trim() : <span className="text-slate-300">-</span>}
                  </p>
                </div>
              </div>

              {/* Trade Info */}
              <div className="pt-4 border-t border-slate-200/60 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Trade Details</span>
                </div>
                <div className="space-y-2 pl-1">
                  <div className="flex justify-between items-center text-[12px] gap-2">
                    <span className="text-slate-700 font-semibold shrink-0">Budget</span>
                    <span className="font-bold text-slate-500 truncate text-right max-w-[150px]" title={budget ? `${budgetCurrency} ${budget}`.trim() : undefined}>
                      {budget ? `${budgetCurrency || ""} ${budget}`.trim() : <span className="text-slate-300">-</span>}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-[12px] gap-2">
                    <span className="text-slate-700 font-semibold shrink-0">Terms</span>
                    <span className="font-bold text-slate-500 truncate text-right max-w-[150px]" title={paymentTerms || undefined}>
                      {paymentTerms || <span className="text-slate-300">-</span>}
                    </span>
                  </div>
                </div>
              </div>

              {/* Logistics Info */}
              <div className="pt-2.5 border-t border-slate-200/60 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Logistics</span>
                </div>
                <div className="space-y-2 pl-1">
                  <div className="flex justify-between items-center text-[12px] gap-2">
                    <span className="text-slate-700 font-semibold shrink-0">Lead Time</span>
                    <span className="font-bold text-slate-500 truncate text-right max-w-[150px]" title={leadTime ? new Date(leadTime).toLocaleDateString() : undefined}>
                      {leadTime ? new Date(leadTime).toLocaleDateString() : <span className="text-slate-300">-</span>}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-[12px] gap-2">
                    <span className="text-slate-700 font-semibold shrink-0">City</span>
                    <span 
                      className="font-bold text-slate-500 truncate text-right max-w-[150px]"
                      title={originLocation ? (originLocation.city || originLocation.name || originLocation.pincode || originLocation.portName) : undefined}
                    >
                      {originLocation ? (originLocation.city || originLocation.name || originLocation.pincode || originLocation.portName) : <span className="text-slate-300">-</span>}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubmitRfqPage
